// Workflow Doctor — agentic chat popup. Free-form Q&A about the live
// workflow. Can produce a fix plan whose steps the user applies via the
// existing AskCode flow (parent owns the AskCode wiring via callback).
//
// Multi-language: never gates UX on regex/keywords.
// Image attachments: paste/drop/upload, in-memory only (mirrors AskCode).

var React = require('react');
var { createElement: h, useEffect, useMemo, useRef, useState } = React;
var doctorChat = require('../lib/doctorChat');
var questionControl = require('./QuestionControl');
var { renderChatMarkdown } = require('../lib/renderChatMarkdown');
var { labelFor, detailFromArgs, outcomeFromResult, dedupeConsecutive } = require('../lib/doctorToolLabels');
var { captureWorkflowCanvas } = require('../lib/captureCanvas');

require('./DoctorChatPopup.css');

var MAX_HISTORY_FOR_DISPLAY = 60;
var IMAGE_MAX_BYTES = 7.5 * 1024 * 1024; // mirrors AskCode

// ---------------------------------------------------------------------------
// Image helpers — read File → dataUrl, used for paste/drop/upload.
// ---------------------------------------------------------------------------

function readFileAsDataUrl(file) {
  return new Promise(function(resolve, reject) {
    if (!file || !/^image\//.test(file.type || '')) {
      return reject(new Error('Not an image'));
    }
    if (file.size > IMAGE_MAX_BYTES) {
      return reject(new Error('Image too large (max 7.5MB)'));
    }
    var reader = new FileReader();
    reader.onload = function() {
      resolve({ dataUrl: String(reader.result || ''), mediaType: file.type || 'image/png' });
    };
    reader.onerror = function() { reject(reader.error || new Error('Read failed')); };
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Tiny SVG icon factory — keeps JSX legible. Each icon is a single-purpose
// pure function so the markup below reads as `Icon.send()` rather than 4
// nested h() calls.
var Icon = {
  // Workflow / "flow" icon — matches the gradient S-wave + arrow used in
  // the workbook explorer panel ([WorkbookExplorerPanel.js fileIcon]).
  // Uses a popup-local gradient id so it doesn't collide.
  flow: function() {
    return h('svg', { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none' },
      h('defs', null,
        h('linearGradient', { id: 'doctor-chat-flow-grad', x1: '0%', y1: '50%', x2: '100%', y2: '50%' },
          h('stop', { offset: '0%', stopColor: '#7dd3fc' }),
          h('stop', { offset: '100%', stopColor: '#38bdf8' })
        )
      ),
      h('path', {
        d: 'M3 14 C 8 6, 14 14, 20 6',
        fill: 'none',
        stroke: 'url(#doctor-chat-flow-grad)',
        strokeWidth: 2.4,
        strokeLinecap: 'round'
      }),
      h('polyline', {
        points: '15 6 20 6 20 11',
        fill: 'none',
        stroke: 'url(#doctor-chat-flow-grad)',
        strokeWidth: 2.4,
        strokeLinecap: 'round',
        strokeLinejoin: 'round'
      })
    );
  },
  send: function() {
    return h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
      h('polyline', { points: '5 12 12 5 19 12' }),
      h('line', { x1: 12, y1: 5, x2: 12, y2: 19 })
    );
  },
  close: function() {
    return h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
      h('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
      h('line', { x1: 6, y1: 6, x2: 18, y2: 18 })
    );
  },
  attach: function() {
    return h('svg', { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
      h('path', { d: 'M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48' })
    );
  },
  doctor: function() {
    return h('svg', { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2 },
      h('path', { d: 'M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0016.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 002 8.5c0 2.29 1.51 4.04 3 5.5l7 7 7-7z' })
    );
  }
};

// Resolve selected-node ids against the live workflow node list and produce
// `[{ id, title, icon, active }, ...]` chips. ALL selected ids are returned
// so the user can toggle them on/off in the strip; `active=false` chips
// render dimmed and are NOT prepended to the next message. Skips ids
// missing from the workflow (e.g. stale selection from a deleted node).
function buildContextNodes(workflowState, selectedIds, excludedIds) {
  if (!workflowState || !Array.isArray(workflowState.nodes)) return [];
  var sel = selectedIds || {};
  var excl = excludedIds || {};
  var byId = {};
  workflowState.nodes.forEach(function(n) { if (n && n.id) byId[n.id] = n; });
  var out = [];
  Object.keys(sel).forEach(function(id) {
    if (!sel[id]) return;
    var n = byId[id];
    if (!n) return;
    out.push({
      id: id,
      title: n.title || n.label || n.name || id,
      icon: n.icon || '',
      active: !excl[id]
    });
  });
  return out;
}

// Format the @-mention block prepended to the user's message. Phrased as
// an explicit focus directive — the agent treats these nodes as the
// PRIMARY subject of the user's question (the equivalent of an @-mention),
// not background context. Only ACTIVE chips are included; toggled-off
// ones are skipped. Returns '' when nothing is active.
function formatContextBlock(contextNodes) {
  var active = (contextNodes || []).filter(function(n) { return n.active !== false; });
  if (active.length === 0) return '';
  var lines = active.map(function(n) {
    return '- @' + n.title + ' (id: ' + n.id + ')';
  });
  var focus = active.length === 1
    ? 'The user is asking specifically about this node. Focus your reply on it.'
    : 'The user is asking specifically about these nodes. Focus your reply on them.';
  return '<mentioned_nodes>\n' + lines.join('\n') + '\n' + focus + '\n</mentioned_nodes>\n\n';
}

// Format the parent's pendingNotice payload into a human-readable assistant
// message. Currently handles { kind: 'queue_complete', applied, skipped,
// totalSteps }. Returns '' for unknown kinds so we don't append junk.
function formatPendingNotice(notice) {
  if (!notice || !notice.kind) return '';
  if (notice.kind === 'queue_complete') {
    var applied = Array.isArray(notice.applied) ? notice.applied : [];
    var skipped = Array.isArray(notice.skipped) ? notice.skipped : [];
    var total = Number(notice.totalSteps) || (applied.length + skipped.length);
    if (applied.length === total && skipped.length === 0) {
      var titles = applied.map(function(a) {
        return '<node id="' + a.nodeId + '">' + (a.nodeTitle || a.nodeId) + '</node>';
      }).join(', ');
      return total === 1
        ? 'Done — applied the change to ' + titles + '.'
        : 'Done — applied all ' + total + ' changes (' + titles + ').';
    }
    if (applied.length === 0) {
      return 'No changes were applied — closed before any step ran.';
    }
    var appliedLine = applied.length > 0
      ? 'Applied: ' + applied.map(function(a) {
          return '<node id="' + a.nodeId + '">' + (a.nodeTitle || a.nodeId) + '</node>';
        }).join(', ')
      : '';
    var skippedLine = skipped.length > 0
      ? 'Skipped: ' + skipped.map(function(s) {
          return '<node id="' + s.nodeId + '">' + (s.nodeTitle || s.nodeId) + '</node>';
        }).join(', ')
      : '';
    return [appliedLine, skippedLine].filter(Boolean).join('\n');
  }
  return '';
}

// Renders a 28px avatar circle. AI = stethoscope glyph, user = first letter
// of "You". Mirrors the planner's planner-message-avatar pattern.
function MessageAvatar(props) {
  var isUser = !!props.isUser;
  if (isUser) {
    return h('div', { className: 'doctor-chat-avatar doctor-chat-avatar--user' }, 'U');
  }
  return h('div', { className: 'doctor-chat-avatar doctor-chat-avatar--ai' }, Icon.doctor());
}

// Helper: returns a markdown renderer pre-bound with the popup's node /
// variable click handlers. Nothing changes when the handlers are null — the
// renderer just emits plain styled text.
function makeMarkdownRenderer(onNodeClick, onVariableClick) {
  return function(text) {
    return renderChatMarkdown(text, 'doctor-chat', {
      onNodeClick: onNodeClick,
      onVariableClick: onVariableClick
    });
  };
}

var STEP_STATUS_ICON = { done: '✓', skipped: '–', active: null };

// One step in a fix plan. status: 'pending' | 'active' | 'done' | 'skipped'
function FixPlanCard(props) {
  var step = props.step;
  var status = props.status || 'pending';
  var cls = 'doctor-chat-step doctor-chat-step--' + status;
  return h('div', { className: cls },
    h('div', { className: 'doctor-chat-step-head' },
      h('span', { className: 'doctor-chat-step-num' }, '#' + step.stepId),
      h('span', { className: 'doctor-chat-step-node' }, step.nodeTitle || step.nodeId),
      h('span', { className: 'doctor-chat-step-status' },
        status === 'active'
          ? h('span', { className: 'doctor-chat-step-spinner' })
          : (STEP_STATUS_ICON[status] || null)
      )
    ),
    step.issue
      ? h('div', { className: 'doctor-chat-step-issue' }, step.issue)
      : null,
    step.askcodePrompt
      ? h('div', { className: 'doctor-chat-step-prompt' }, step.askcodePrompt)
      : null
  );
}

// Single button at the bottom of a fix plan. One click → AskCode opens for
// step 1, when the user finishes that step the parent advances to step 2,
// and so on. Disabled once clicked so the queue can't be double-started.
function ApprovePlanBar(props) {
  var steps = Array.isArray(props.steps) ? props.steps : [];
  var onApprove = props.onApprove || function() {};
  var started = !!props.started;
  if (steps.length === 0) return null;
  return h('div', { className: 'doctor-chat-plan-approve' },
    h('button', {
      className: 'doctor-chat-plan-approve-btn',
      onClick: function() { if (!started) onApprove(steps); },
      disabled: started
    }, started
        ? (steps.length > 1 ? 'Approved — applying ' + steps.length + ' steps…' : 'Approved')
        : (steps.length > 1 ? 'Approve all ' + steps.length + ' steps' : 'Approve'))
  );
}

// Renders the body of an assistant message — markdown OR a question control
// when the model embedded <question>/<input> tags. Pulled out of MessageBubble
// so the bubble itself stays a flat layout.
function renderAssistantBody(msg, renderMd, isInteractive, onAnswerSubmit) {
  var parsed = questionControl.parseQuestionTags(msg.content);
  var hasQuestion = parsed.parts.some(function(p) {
    return p.type === 'question' || p.type === 'input';
  });
  if (hasQuestion) {
    return h(questionControl.QuestionControl, {
      parts: parsed.parts,
      interactive: isInteractive,
      // Markdown-render prose segments AROUND <question> blocks so
      // **bold**, `code`, bullets, AND <node>/<variable> chips all render.
      renderText: renderMd,
      onSubmit: onAnswerSubmit
    });
  }
  return renderMd(msg.content);
}

// Renders an inline image strip (used by both user and assistant messages
// when the message carries attached images).
function renderMessageImages(images) {
  if (!images || images.length === 0) return null;
  return h('div', { className: 'doctor-chat-msg-images' },
    images.map(function(img, i) {
      return h('img', {
        key: i,
        className: 'doctor-chat-msg-image',
        src: img.dataUrl,
        alt: 'attachment'
      });
    })
  );
}

// User and assistant messages render as flat sidebar rows (avatar + sender
// label + plain text). Both bubbles are transparent — the avatar
// disambiguates speaker, and a narrow sidebar reads better all-left-aligned.
function MessageBubble(props) {
  var msg = props.message;
  var isInteractive = !!props.interactive;
  var onAnswerSubmit = props.onAnswerSubmit || function() {};
  var onNodeClick = props.onNodeClick || null;
  var onVariableClick = props.onVariableClick || null;
  var renderMd = makeMarkdownRenderer(onNodeClick, onVariableClick);
  var isUser = msg.role === 'user';
  var images = Array.isArray(msg.images) ? msg.images : [];

  var bodyContent;
  if (isUser || !msg.content) {
    // User messages render as plain text — show displayContent (the user's
    // raw prose) when present so the prepended <context> block stays
    // server-only. Falls back to .content for legacy messages without it.
    bodyContent = (msg.displayContent != null ? msg.displayContent : msg.content) || null;
  } else {
    bodyContent = renderAssistantBody(msg, renderMd, isInteractive, onAnswerSubmit);
  }

  var bubbleClass = 'doctor-chat-msg-bubble doctor-chat-msg-bubble--' +
    (isUser ? 'user' : 'assistant');

  // Small subtle chips inside the user's bubble, above the prose. Unlike
  // the prominent attachment cards in the input strip, these are tiny
  // pill-shaped tags — quiet enough to not steal attention from the
  // message itself but still recognisable as "node references."
  var sentAttachments = (isUser && Array.isArray(msg.contextNodes) && msg.contextNodes.length > 0)
    ? h('div', { className: 'doctor-chat-msg-mentions' },
        msg.contextNodes.map(function(n) {
          return h('span', { key: n.id, className: 'doctor-chat-msg-mention' }, n.title);
        })
      )
    : null;

  return h('div', { className: 'doctor-chat-msg doctor-chat-msg--' + (isUser ? 'user' : 'ai') },
    h(MessageAvatar, { isUser: isUser }),
    h('div', { className: 'doctor-chat-msg-content' },
      h('div', { className: 'doctor-chat-msg-sender' }, isUser ? 'You' : 'Doctor'),
      h('div', { className: bubbleClass },
        sentAttachments,
        renderMessageImages(images),
        bodyContent
      )
    )
  );
}

// Single trace row. `trace` is { name, count, details, outcome } where
// count >= 1, details is an array of subjects this row covers (one per
// distinct call), and outcome is the freshest one-line result summary.
// Renders icon + label + ":" + details + " — " + outcome + optional ×N.
// `latest` flag highlights the row currently in flight.
function ToolTraceLine(props) {
  var trace = props.trace || {};
  var latest = !!props.latest;
  var meta = labelFor(trace.name);
  var rowClass = 'doctor-chat-trace' + (latest ? ' doctor-chat-trace--active' : ' doctor-chat-trace--done');
  // Cap the visible subjects so a 12-node read run doesn't push the chip
  // off-screen. Anything past the cap collapses into "+N more".
  var details = Array.isArray(trace.details) ? trace.details : [];
  var SHOW_DETAIL_CAP = 3;
  var visibleDetails = details.slice(0, SHOW_DETAIL_CAP);
  var hiddenDetailCount = Math.max(0, details.length - SHOW_DETAIL_CAP);
  var detailText = visibleDetails.join(', ') + (hiddenDetailCount > 0 ? ', +' + hiddenDetailCount + ' more' : '');

  return h('div', { className: rowClass },
    h('span', { className: 'doctor-chat-trace-icon' }, meta.icon),
    h('span', { className: 'doctor-chat-trace-label' }, meta.label),
    detailText
      ? h('span', { className: 'doctor-chat-trace-detail', title: details.join(', ') }, ': ' + detailText)
      : null,
    trace.outcome
      ? h('span', { className: 'doctor-chat-trace-outcome' }, ' — ' + trace.outcome)
      : null,
    trace.count > 1
      ? h('span', { className: 'doctor-chat-trace-count' }, '×' + trace.count)
      : null,
    latest
      ? h('span', { className: 'doctor-chat-trace-dots', 'aria-hidden': 'true' },
          h('span', null, '.'),
          h('span', null, '.'),
          h('span', null, '.')
        )
      : h('span', { className: 'doctor-chat-trace-check', 'aria-hidden': 'true' }, '✓')
  );
}

// Renders the strip of selected-node chips that sits directly above the
// input. The whole chip is a toggle — clicking flips it between active
// (full opacity, included in next message) and inactive (0.5 opacity,
// excluded). No explicit × button — the dimmed state IS the "off" state.
function ContextChipStrip(props) {
  var nodes = props.nodes || [];
  var onToggle = props.onToggle || function() {};
  if (nodes.length === 0) return null;
  return h('div', { className: 'doctor-chat-context-strip' },
    nodes.map(function(n) {
      var cls = 'doctor-chat-context-attachment'
        + (n.active === false ? ' doctor-chat-context-attachment--inactive' : '');
      return h('button', {
        key: n.id,
        type: 'button',
        className: cls,
        title: n.active === false
          ? 'Click to mention this node in your next message'
          : 'Click to skip this node in your next message',
        onClick: function() { onToggle(n.id); }
      },
        h('span', { className: 'doctor-chat-context-attachment-icon' }, Icon.flow()),
        h('span', { className: 'doctor-chat-context-attachment-title' }, n.title)
      );
    })
  );
}

// ---------------------------------------------------------------------------
// Main popup
// ---------------------------------------------------------------------------

function DoctorChatPopup(props) {
  var workflowState = props.workflowState;
  var executionLog = props.executionLog || null;
  var onClose = props.onClose || function() {};
  // Parent wires this — typically opens the node's AskCode panel with the
  // generationPrompt prefilled. Kept for backward compat (single-step fallback).
  var onApplyWithAskCode = props.onApplyWithAskCode || null;
  // Preferred: parent applies the whole ordered list sequentially, opening
  // AskCode for one step at a time and advancing only when the previous one
  // closes. Avoids the per-step button explosion in the UI.
  var onApplyAllWithAskCode = props.onApplyAllWithAskCode || null;
  // Click handlers for inline <node id="..."> and <variable name="..."> chips
  // in assistant replies. Wired by the canvas: onLocateNode pans+zooms+
  // selects the node; onHighlightVariable highlights every node that touches
  // the variable. Both no-ops if the parent didn't pass them.
  var onLocateNode = typeof props.onLocateNode === 'function' ? props.onLocateNode : null;
  var onHighlightVariable = typeof props.onHighlightVariable === 'function' ? props.onHighlightVariable : null;
  // Live canvas selection (object map of nodeId → true). When the user has
  // node(s) selected, they're rendered as removable chips above the input
  // and prepended to the next message as a <context> block.
  var selectedNodeIds = props.selectedNodeIds || {};
  // Push-driven notice from the parent — currently used to surface the
  // result of an Approve fix-plan queue once every step has either applied
  // or been skipped. The popup converts it into an assistant message and
  // calls onConsumePendingNotice to clear the parent's state.
  var pendingNotice = props.pendingNotice || null;
  var onConsumePendingNotice = typeof props.onConsumePendingNotice === 'function'
    ? props.onConsumePendingNotice
    : null;
  // Live step execution status from App.js queue runner.
  // Shape: { activeNodeId: string|null, completed: {[nodeId]: 'done'|'skipped'} }
  var fixStatus = props.fixStatus || null;
  // Parent-injected handler for the runWorkflow client tool. Shape:
  //   () => Promise<{ status, executionLog, durationMs }>
  // Resolves once the canvas execution reaches a terminal status (succeeded /
  // failed / stopped). When omitted, the runWorkflow tool returns an error
  // and the agent falls back to static analysis.
  var onRunWorkflow = typeof props.onRunWorkflow === 'function' ? props.onRunWorkflow : null;
  // Ref to the live canvas DOM element. Used once per session (on the first
  // user message) to capture a PNG of the canvas as supplemental visual
  // context for the doctor model. Optional — if absent, we skip the snapshot
  // and the model still has the full workflow JSON via its tools.
  var canvasRef = props.canvasRef || null;

  var [messages, setMessages] = useState([]);            // [{role, content, images?}]
  var [pendingImages, setPendingImages] = useState([]);  // [{dataUrl, mediaType}]
  var [input, setInput] = useState('');
  var [busy, setBusy] = useState(false);
  var [error, setError] = useState(null);
  var [trace, setTrace] = useState([]);                  // current-turn tool trace
  // Live streamed text for the CURRENT model round — the doctor's thinking as it
  // types. Reset on each new round (server emits a 'round' event) and cleared
  // when the turn ends. Shown under the spinner instead of a static "Thinking...".
  var [liveThinking, setLiveThinking] = useState('');
  // Tracks which fix-plan messages have had Approve clicked (key = planIdx).
  // Used to disable the Approve button after the first click.
  var [approvedPlans, setApprovedPlans] = useState({});
  // Excluded chips: ids the user toggled OFF by clicking the chip. Excluded
  // chips render at 0.5 opacity and are NOT prepended to the next message.
  // Clicking again toggles them back on. Stays per-popup-session.
  var [excludedContextIds, setExcludedContextIds] = useState({});
  // Pending confirmation chip for runWorkflow. When the agent calls the tool,
  // we set this to true and stash the resolver below; the chip in the message
  // log calls resolveRunConfirm(true|false) which both settles the awaited
  // promise and flips the state back to false. Tool flow:
  //   doctorChatTools.runWorkflow → ctx.requestRunWorkflowConfirm()
  //     → setRunConfirmPending(true) + new Promise stashed in resolver ref
  //     → user clicks Run / Skip → resolveRunConfirm fires → tool proceeds.
  var [runConfirmPending, setRunConfirmPending] = useState(false);
  // Live status while the run is in flight ('Running...' chip). Cleared once
  // the run resolves so the message log returns to normal flow.
  var [runInFlight, setRunInFlight] = useState(false);

  var scrollRef = useRef(null);
  var fileInputRef = useRef(null);
  var inputRef = useRef(null);
  // Holds the pending Promise resolver for the run-confirm chip. Stored in a
  // ref (not state) because a re-render must NOT replace the in-flight
  // resolver — that would orphan the awaited promise inside the tool handler.
  var runConfirmResolverRef = useRef(null);

  // Autofocus the textarea on open. The popup is conditionally mounted by
  // WorkflowCanvas (`doctorChatOpen ? h(DoctorChatPopup, ...) : null`), so
  // mount === open — this fires exactly once per open. requestAnimationFrame
  // waits for the slide-in animation's first frame so the focus doesn't get
  // stolen back by whatever had focus before the click.
  useEffect(function() {
    var raf = requestAnimationFrame(function() {
      if (inputRef.current) inputRef.current.focus();
    });
    return function() { cancelAnimationFrame(raf); };
  }, []);

  // Watch for pendingNotice from the parent (Approve queue completion etc.)
  // — convert into an assistant message and clear the parent's state so the
  // notice fires exactly once. appendMessage is stable (uses functional
  // setState) so we only need pendingNotice identity in the dep list.
  useEffect(function() {
    if (!pendingNotice) return;
    var text = formatPendingNotice(pendingNotice);
    if (text) {
      appendMessage({ role: 'assistant', content: text });
    }
    if (onConsumePendingNotice) onConsumePendingNotice();
  }, [pendingNotice]);

  // Always scroll to bottom on new content while the user hasn't scrolled up.
  var pinnedRef = useRef(true);
  useEffect(function() {
    var el = scrollRef.current;
    if (!el) return;
    function onScroll() {
      var distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      pinnedRef.current = distance < 16;
    }
    el.addEventListener('scroll', onScroll);
    return function() { el.removeEventListener('scroll', onScroll); };
  }, []);
  useEffect(function() {
    var el = scrollRef.current;
    if (!el) return;
    if (pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [messages, busy, trace]);

  // Resolve selected-node ids → chip data on every render. Cheap O(N) over
  // node count, and we want it to track live canvas selection changes.
  var contextNodes = buildContextNodes(workflowState, selectedNodeIds, excludedContextIds);

  // requestRunWorkflowConfirm — opens the in-chat chip and returns a Promise
  // that resolves to true (user clicked Run) or false (user clicked Skip).
  // Re-entrancy guard: if a confirm is already pending, reject the second
  // request so we never overwrite the live resolver.
  function requestRunWorkflowConfirm() {
    return new Promise(function(resolve) {
      if (runConfirmResolverRef.current) {
        return resolve(false);
      }
      runConfirmResolverRef.current = resolve;
      setRunConfirmPending(true);
    });
  }

  function resolveRunConfirm(decision) {
    var resolver = runConfirmResolverRef.current;
    runConfirmResolverRef.current = null;
    setRunConfirmPending(false);
    if (typeof resolver === 'function') resolver(!!decision);
  }

  // Unmount cleanup. If the popup closes while a run-confirm is pending
  // (user dismissed the chat mid-question), we must resolve the orphaned
  // promise as Skip — otherwise the awaiting tool handler in
  // doctorChat.loopUntilFinal hangs forever and the agent stalls.
  useEffect(function() {
    return function() {
      var resolver = runConfirmResolverRef.current;
      if (typeof resolver === 'function') {
        runConfirmResolverRef.current = null;
        resolver(false);
      }
    };
  }, []);

  // runWorkflow ctx fn — wraps the parent's onRunWorkflow with the in-flight
  // status flag. If the parent didn't wire onRunWorkflow we throw; the tool
  // handler turns that into an error result the agent can read.
  async function runWorkflowCtx() {
    if (!onRunWorkflow) throw new Error('runWorkflow handler not wired');
    setRunInFlight(true);
    try {
      return await onRunWorkflow();
    } finally {
      setRunInFlight(false);
    }
  }

  // getCtx is re-evaluated by doctorChat on every resume so live edits land
  // mid-conversation.
  var getCtx = useMemo(function() {
    return function() {
      return {
        workflowState: workflowState,
        executionLog: executionLog,
        // Both async — the runWorkflow tool awaits them. Sync tools ignore.
        requestRunWorkflowConfirm: requestRunWorkflowConfirm,
        runWorkflow: runWorkflowCtx
      };
    };
    // requestRunWorkflowConfirm + runWorkflowCtx close over onRunWorkflow and
    // the ref/setters which are stable; recompute when workflowState changes.
  }, [workflowState, executionLog, onRunWorkflow]);

  function appendMessage(msg) {
    setMessages(function(prev) {
      var next = prev.concat([msg]);
      if (next.length > MAX_HISTORY_FOR_DISPLAY) next = next.slice(next.length - MAX_HISTORY_FOR_DISPLAY);
      return next;
    });
  }

  // First-message-only canvas snapshot. Skipped when the user already
  // attached an image (their attachment is the visual context they want the
  // model to focus on) and when no canvasRef was wired through. Best-effort
  // — never throws; returns null on any render failure so the chat send
  // proceeds either way.
  async function maybeCaptureCanvasSnapshot(isFirstMessage, userAttachedAny) {
    if (!isFirstMessage) return null;
    if (userAttachedAny) return null;
    if (!canvasRef || !canvasRef.current) return null;
    try {
      return await captureWorkflowCanvas(canvasRef.current);
    } catch (_) {
      return null;
    }
  }

  // Core send: append a user message and call the server. Used by the input
  // bar AND by the Q&A submit button so both paths share the busy/trace/error
  // bookkeeping in one place.
  async function sendUserMessage(text, attachedImages, attachedContext) {
    if (busy) return;
    var trimmed = String(text || '').trim();
    var imgs = Array.isArray(attachedImages) ? attachedImages : [];
    var ctxNodes = Array.isArray(attachedContext) ? attachedContext : [];
    if (!trimmed && imgs.length === 0) return;

    // First-turn canvas snapshot (auto). Prepended to imgs so the user-pasted
    // images (if any future first-turn variant ever passes them through this
    // guard) still come last in the model's view. Skipped when the user
    // already attached anything — their image is the focus, not the canvas.
    var isFirstMessage = messages.length === 0;
    var canvasSnapshot = await maybeCaptureCanvasSnapshot(isFirstMessage, imgs.length > 0);
    if (canvasSnapshot) imgs = [canvasSnapshot].concat(imgs);

    // Build two versions of the message:
    //   - displayContent: what the user sees in the chat log (their prose).
    //     The selected-node chips render as a small caption ABOVE the bubble
    //     so the raw <context> XML never appears in the UI.
    //   - serverContent: what we send to the agent. Prepends a <context>
    //     block with the selected node ids/titles so the agent can call
    //     getNode() on them directly without guessing.
    var contextBlock = formatContextBlock(ctxNodes);
    var serverContent = contextBlock + trimmed;

    var userMsg = {
      role: 'user',
      content: serverContent,
      displayContent: trimmed,
      contextNodes: ctxNodes
    };
    if (imgs.length > 0) userMsg.images = imgs;

    var nextMessages = messages.concat([userMsg]);
    setMessages(nextMessages);
    setError(null);
    setTrace([]);
    setLiveThinking('');
    setBusy(true);

    try {
      // Strip UI-only fields (displayContent, contextNodes) before sending —
      // the server validates the message shape and rejects unknown keys.
      // Only role/content/images are wire-allowed.
      var wireMessages = nextMessages.map(function(m) {
        var out = { role: m.role, content: m.content };
        if (Array.isArray(m.images) && m.images.length > 0) out.images = m.images;
        return out;
      });
      var result = await doctorChat.sendMessageStream({
        getCtx: getCtx,
        // Server only needs the LAST 8 messages, but the chat lib forwards
        // whatever we send and the server slices internally.
        messages: wireMessages,
        images: imgs,
        // Live thinking: append text deltas for the current round, hiding the
        // machine-readable <tool> block so the user only sees readable prose.
        onChunk: function(ev) {
          var full = (ev && ev.fullText) || '';
          var cut = full.indexOf('<tool');
          setLiveThinking(cut >= 0 ? full.slice(0, cut) : full);
        },
        // New model round → clear the previous round's live text.
        onRound: function() { setLiveThinking(''); },
        onClientTool: function(t) {
          // The transport invokes this callback ONCE per client-tool round
          // with { name, args, result } populated. We record both the input
          // subject (detailFromArgs) and a one-line result summary
          // (outcomeFromResult) so the trace UI can show "Reading a node:
          // Format Slack Message — found" instead of a bare "Reading a
          // node ×4". Empty strings are fine — the renderer hides them.
          setTrace(function(prev) {
            return prev.concat([{
              name: t.name,
              detail: detailFromArgs(t.name, t.args),
              outcome: outcomeFromResult(t.name, t.result)
            }]);
          });
        }
      });

      var assistantMsg = { role: 'assistant', content: result.reply || '' };
      if (result.fixPlan && Array.isArray(result.fixPlan.steps) && result.fixPlan.steps.length > 0) {
        assistantMsg.fixPlan = result.fixPlan;
      }
      appendMessage(assistantMsg);
    } catch (err) {
      setError((err && err.message) || 'Chat failed');
    } finally {
      setBusy(false);
      setTrace([]);
      setLiveThinking('');
    }
  }

  async function handleSend() {
    var text = String(input || '').trim();
    if (busy) return;
    if (!text && pendingImages.length === 0) return;

    var sentImages = pendingImages;
    // Only the active (non-dimmed) chips become attachments on this turn.
    var sentContext = contextNodes.filter(function(n) { return n.active !== false; });
    setInput('');
    setPendingImages([]);
    // Auto-toggle the chips we just sent to inactive — dimmed in the strip
    // so the same nodes don't silently re-attach to the next message. The
    // user can click them to re-enable, or click another node to attach
    // a different one. The chip is NOT removed (the canvas selection is
    // unchanged), just visually muted + excluded from future sends.
    if (sentContext.length > 0) {
      setExcludedContextIds(function(prev) {
        var next = Object.assign({}, prev);
        sentContext.forEach(function(n) { next[n.id] = true; });
        return next;
      });
    }
    await sendUserMessage(text, sentImages, sentContext);
  }

  // Called when the user clicks "Submit Answers" inside an assistant message's
  // Q&A control. The control passes "<question>: <answer>\n..." text.
  function handleAnswerSubmit(answerText) {
    if (busy) return;
    sendUserMessage(answerText, []);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  async function addImagesFromFiles(fileList) {
    if (!fileList || !fileList.length) return;
    var added = [];
    for (var i = 0; i < fileList.length; i++) {
      try {
        var img = await readFileAsDataUrl(fileList[i]);
        added.push(img);
      } catch (e) {
        setError(e.message || 'Image read failed');
      }
    }
    if (added.length) setPendingImages(function(prev) { return prev.concat(added); });
  }

  function handlePaste(e) {
    if (!e.clipboardData) return;
    var items = e.clipboardData.items || [];
    var files = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        var f = items[i].getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault();
      addImagesFromFiles(files);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    if (e.dataTransfer && e.dataTransfer.files) {
      addImagesFromFiles(e.dataTransfer.files);
    }
  }

  // Single-button approval: hand the entire ordered step list to the parent,
  // which opens AskCode for step 1, then for step 2 after the user closes
  // step 1's editor, and so on. Falls back to per-step apply if the parent
  // only wired the legacy single-step handler.
  function handleApprovePlan(steps, planMessageIdx) {
    var list = Array.isArray(steps) ? steps : [];
    if (list.length === 0) return;
    setApprovedPlans(function(prev) {
      var next = Object.assign({}, prev);
      next[planMessageIdx] = true;
      return next;
    });
    try {
      if (typeof onApplyAllWithAskCode === 'function') {
        onApplyAllWithAskCode(list.map(function(s) {
          return {
            nodeId: s.nodeId,
            nodeTitle: s.nodeTitle,
            askcodePrompt: s.askcodePrompt
          };
        }));
        return;
      }
      if (typeof onApplyWithAskCode === 'function') {
        // Legacy fallback — only the first step actually opens AskCode.
        var first = list[0];
        onApplyWithAskCode({
          nodeId: first.nodeId,
          nodeTitle: first.nodeTitle,
          askcodePrompt: first.askcodePrompt
        });
        return;
      }
      setError('AskCode handler not wired by parent.');
    } catch (err) {
      setError((err && err.message) || 'Apply failed');
    }
  }

  function stop(e) { e.stopPropagation(); }

  return h('div', {
    className: 'doctor-chat-popup',
    onMouseDown: stop, onTouchStart: stop, onClick: stop,
    onDragOver: function(e) { e.preventDefault(); },
    onDrop: handleDrop
  },
    h('div', { className: 'doctor-chat-header' },
      h('div', { className: 'doctor-chat-header-left' },
        h('div', { className: 'doctor-chat-title' }, 'Workflow Doctor'),
        h('span', { className: 'doctor-chat-badge' }, 'Beta')
      ),
      h('button', { className: 'doctor-chat-close', onClick: onClose, title: 'Close' },
        Icon.close()
      )
    ),

    h('div', { className: 'doctor-chat-body', ref: scrollRef },
      messages.length === 0
        ? h('div', { className: 'doctor-chat-empty' },
            'Ask anything about your workflow. I can find broken nodes, explain logic, and propose a fix plan.'
          )
        : (function() {
            // Find the LAST assistant message — only that bubble's Q&A is
            // interactive. Older question buttons render as static labels.
            var lastAssistantIdx = -1;
            for (var i = messages.length - 1; i >= 0; i--) {
              if (messages[i].role === 'assistant') { lastAssistantIdx = i; break; }
            }
            return messages.map(function(m, idx) {
              var isLatestAssistant = idx === lastAssistantIdx;
              return h(React.Fragment, { key: idx },
                h(MessageBubble, {
                  message: m,
                  interactive: isLatestAssistant && !busy,
                  onAnswerSubmit: handleAnswerSubmit,
                  onNodeClick: onLocateNode,
                  onVariableClick: onHighlightVariable
                }),
                m.fixPlan
                  ? h('div', { className: 'doctor-chat-plan' },
                      m.fixPlan.summary
                        ? h('div', { className: 'doctor-chat-plan-summary' }, m.fixPlan.summary)
                        : null,
                      m.fixPlan.steps.map(function(step) {
                        var stepStatus = 'pending';
                        if (approvedPlans[idx] && fixStatus) {
                          var completed = fixStatus.completed || {};
                          if (completed[step.nodeId] === 'done') stepStatus = 'done';
                          else if (completed[step.nodeId] === 'skipped') stepStatus = 'skipped';
                          else if (fixStatus.activeNodeId === step.nodeId) stepStatus = 'active';
                        } else if (approvedPlans[idx]) {
                          stepStatus = 'pending';
                        }
                        return h(FixPlanCard, {
                          key: step.stepId,
                          step: step,
                          status: stepStatus
                        });
                      }),
                      h(ApprovePlanBar, {
                        steps: m.fixPlan.steps,
                        started: !!approvedPlans[idx],
                        onApprove: function(steps) { handleApprovePlan(steps, idx); }
                      })
                    )
                  : null
              );
            });
          })(),

      busy
        ? h('div', { className: 'doctor-chat-status' },
            h('div', { className: 'doctor-chat-status-head' },
              h('span', { className: 'doctor-chat-spinner' }),
              h('span', { className: 'doctor-chat-status-text' }, 'Thinking...')
            ),
            // Live streamed thinking for the current round (empty until the
            // first token arrives, so the spinner shows alone at first).
            liveThinking
              ? h('div', { className: 'doctor-chat-live-thinking' }, liveThinking)
              : null,
            (function() {
              // Collapse runs of the same tool ("getNode × 3" instead of three rows).
              // The LAST entry is "in flight"; previous entries are done.
              var rows = dedupeConsecutive(trace);
              if (rows.length === 0) return null;
              return h('div', { className: 'doctor-chat-trace-list' },
                rows.map(function(t, i) {
                  return h(ToolTraceLine, {
                    key: i,
                    trace: t,
                    latest: i === rows.length - 1
                  });
                })
              );
            })()
          )
        : null,

      error
        ? h('div', { className: 'doctor-chat-error' }, error)
        : null,

      // Run-confirm chip — shown when the agent calls runWorkflow and we're
      // waiting on the user to click Run / Skip. Side-effect-aware copy: tell
      // the user the run will hit real APIs so they don't approve blindly.
      runConfirmPending
        ? h('div', { className: 'doctor-chat-run-confirm' },
            h('div', { className: 'doctor-chat-run-confirm-head' }, 'Run the workflow to gather live evidence?'),
            h('div', { className: 'doctor-chat-run-confirm-body' },
              'This presses Play and waits for the run to finish. Real API calls fire (Slack messages, sheet writes, etc.).'),
            h('div', { className: 'doctor-chat-run-confirm-actions' },
              h('button', {
                className: 'doctor-chat-run-confirm-skip',
                type: 'button',
                onClick: function() { resolveRunConfirm(false); }
              }, 'Skip'),
              h('button', {
                className: 'doctor-chat-run-confirm-go',
                type: 'button',
                onClick: function() { resolveRunConfirm(true); }
              }, 'Run')
            )
          )
        : null,

      // In-flight run banner. Once the user clicks Run, runConfirmPending
      // flips off and the parent's onRunWorkflow promise drives runInFlight.
      runInFlight
        ? h('div', { className: 'doctor-chat-status' },
            h('div', { className: 'doctor-chat-status-head' },
              h('span', { className: 'doctor-chat-spinner' }),
              h('span', { className: 'doctor-chat-status-text' }, 'Running your workflow...')
            )
          )
        : null
    ),

    pendingImages.length > 0
      ? h('div', { className: 'doctor-chat-pending-images' },
          pendingImages.map(function(img, i) {
            return h('div', { key: i, className: 'doctor-chat-pending-image-wrap' },
              h('img', { src: img.dataUrl, alt: 'pending', className: 'doctor-chat-pending-image' }),
              h('button', {
                className: 'doctor-chat-pending-remove',
                onClick: function() {
                  setPendingImages(function(prev) {
                    return prev.filter(function(_, idx) { return idx !== i; });
                  });
                }
              }, '✕')
            );
          })
        )
      : null,

    h(ContextChipStrip, {
      nodes: contextNodes,
      onToggle: function(id) {
        setExcludedContextIds(function(prev) {
          var next = Object.assign({}, prev);
          if (next[id]) delete next[id]; else next[id] = true;
          return next;
        });
      }
    }),

    h('div', { className: 'doctor-chat-input-container' },
      h('div', { className: 'doctor-chat-input-row' },
        h('textarea', {
          ref: inputRef,
          className: 'doctor-chat-input',
          value: input,
          placeholder: busy ? 'Working...' : 'Ask about the workflow...',
          onChange: function(e) { setInput(e.target.value); },
          onKeyDown: handleKeyDown,
          onPaste: handlePaste,
          rows: 2,
          disabled: busy,
          spellCheck: false,
          autoFocus: true
        }),
        h('input', {
          ref: fileInputRef,
          type: 'file',
          accept: 'image/*',
          multiple: true,
          style: { display: 'none' },
          onChange: function(e) {
            addImagesFromFiles(e.target.files);
            e.target.value = '';
          }
        })
      ),
      h('div', { className: 'doctor-chat-input-toolbar' },
        h('button', {
          className: 'doctor-chat-attach-btn',
          title: 'Attach image',
          onClick: function() { if (fileInputRef.current) fileInputRef.current.click(); },
          disabled: busy
        }, Icon.attach()),
        h('button', {
          className: 'doctor-chat-send-btn' + (busy ? ' doctor-chat-send-btn-loading' : ''),
          onClick: handleSend,
          disabled: busy || (!input.trim() && pendingImages.length === 0),
          title: 'Send (Enter)'
        }, Icon.send())
      )
    )
  );
}

module.exports = DoctorChatPopup;
